import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Typography from '@material-ui/core/Typography'
import { makeStyles } from '@material-ui/core/styles'
import CheckIcon from 'components/material-symbol/icons/Check'
import {
  CALLOUT_BACKGROUND,
  CALLOUT_FOREGROUND,
  OUTLINED_INPUT_FOCUS_BORDER,
} from 'lib/form-field-styles'
import { SEGMENTED_BUTTON_BORDER } from 'lib/segmented-button-styles'
import {
  composeOptionBorder,
  composeOptionInteractionStates,
  composeSelectedCalloutSurface,
  SELECTED_CHIP_FOREGROUND,
} from 'pages/pga-coach/book-student/coach-book-student-compose-styles'

// Same card as the booking-type offering cards, with a check in place of the
// radio.
const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    boxSizing: 'border-box',
    width: '100%',
    margin: 0,
    padding: theme.spacing(2, 2.5),
    borderRadius: 12,
    border: composeOptionBorder,
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
    textAlign: 'left',
    font: 'inherit',
    transition: 'border-color 0.15s ease, background-color 0.15s ease',
    ...composeOptionInteractionStates,
    [theme.breakpoints.down('xs')]: {
      padding: theme.spacing(1.75, 2),
      gap: theme.spacing(1.5),
    },
  },
  selected: {
    ...composeSelectedCalloutSurface,
    '& $icon': {
      backgroundColor: theme.palette.common.white,
      color: SELECTED_CHIP_FOREGROUND,
    },
    '& $title': {
      color: SELECTED_CHIP_FOREGROUND,
    },
  },
  disabled: {
    cursor: 'default',
    opacity: 0.6,
    '&, &:hover': {
      borderColor: SEGMENTED_BUTTON_BORDER,
      backgroundColor: theme.palette.common.white,
    },
  },
  icon: {
    width: 32,
    height: 32,
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: CALLOUT_BACKGROUND,
    color: CALLOUT_FOREGROUND,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& .MuiSvgIcon-root, & svg': {
      width: 16,
      height: 16,
      fontSize: 16,
    },
  },
  body: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    margin: 0,
    fontSize: '0.9375rem',
    fontWeight: 600,
    lineHeight: 1.3,
    color: CALLOUT_FOREGROUND,
  },
  description: {
    margin: theme.spacing(0.5, 0, 0),
    fontSize: '0.8125rem',
    lineHeight: 1.4,
    color: theme.palette.text.secondary,
  },
  check: {
    flexShrink: 0,
    width: 24,
    height: 24,
    color: OUTLINED_INPUT_FOCUS_BORDER,
  },
}))

export default function OfferingCard({
  title,
  description,
  icon,
  selected,
  disabled,
  onClick,
  'data-testid': dataTestId,
}) {
  const classes = useStyles()

  return (
    <button
      type="button"
      className={clsx(
        classes.card,
        selected && classes.selected,
        disabled && classes.disabled,
      )}
      aria-pressed={selected}
      disabled={disabled}
      onClick={onClick}
      data-testid={dataTestId}
    >
      <span className={classes.icon} aria-hidden="true">
        {icon}
      </span>
      <span className={classes.body}>
        <Typography component="span" display="block" className={classes.title}>
          {title}
        </Typography>
        {description ? (
          <Typography
            component="span"
            display="block"
            className={classes.description}
          >
            {description}
          </Typography>
        ) : null}
      </span>
      {selected ? (
        <CheckIcon
          className={classes.check}
          data-testid={`${dataTestId}-check`}
          aria-hidden
        />
      ) : null}
    </button>
  )
}

OfferingCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  icon: PropTypes.node,
  selected: PropTypes.bool,
  disabled: PropTypes.bool,
  onClick: PropTypes.func.isRequired,
  'data-testid': PropTypes.string.isRequired,
}
